// src/sizing/buildSizingRequestFromState.ts
// Extracted from SizingPage.tsx to enable reuse in Cost Simulator

import { buildMcRequest } from '@/sizing/buildMcRequest';
import { resolveEffectivePersona } from '@/lib/personaResolver';
import type { NewSizingRequest } from '@/sizing/newSizingEngine';
import type { PersonaProfile } from '@/types/personas';
import type { CatalogData, Tradeoffs, UserType, AdvancedControls } from '@/types';

const BYTES_PER_GIB = 1024 ** 3;

interface BuildSizingRequestOptions {
  catalog: CatalogData;
  personaProfiles: PersonaProfile[];
  tradeoffs: Tradeoffs;
  users: UserType[];
  advanced: AdvancedControls;
  selectedModelId: string | null;
}

const toNum = (value: unknown): number | null => {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
};

const parseJson = <T,>(value: unknown): T | null => {
  if (!value) return null;
  try {
    return typeof value === 'string' ? (JSON.parse(value) as T) : (value as T);
  } catch {
    return null;
  }
};

const resolvePersonaLatency = (
  profile: PersonaProfile | undefined,
  mode: 'snappy' | 'fast' | 'resp' | 'comfy' | 'async' | 'responsive',
) => {
  if (!profile) {
    return { ttftSec: 0, decodeTokPerSec: 0 };
  }

  const key = mode === 'responsive' ? 'resp' : mode;

  const ttftMsByKey: Record<'snappy' | 'fast' | 'resp' | 'comfy' | 'async', number | null> = {
    snappy: toNum((profile as any).ttft_snappy_ms),
    fast: toNum((profile as any).ttft_fast_ms),
    resp: toNum((profile as any).ttft_resp_ms),
    comfy: toNum((profile as any).ttft_comfy_ms),
    async: toNum((profile as any).ttft_async_ms),
  };

  const decodeTpsByKey: Record<'snappy' | 'fast' | 'resp' | 'comfy' | 'async', number | null> = {
    snappy: toNum((profile as any).decode_tps_snappy),
    fast: toNum((profile as any).decode_tps_fast),
    resp: toNum((profile as any).decode_tps_resp),
    comfy: toNum((profile as any).decode_tps_comfy),
    async: toNum((profile as any).decode_tps_async),
  };

  const ttftMs =
    ttftMsByKey[key] ??
    toNum(profile.default_latency_slo_ms) ??
    0;

  const decodeTokPerSec = decodeTpsByKey[key] ?? 0;
  const ttftSec = ttftMs / 1000;

  return { ttftSec, decodeTokPerSec };
};

export function buildSizingRequestFromState({
  catalog,
  personaProfiles,
  tradeoffs,
  users,
  advanced,
  selectedModelId,
}: BuildSizingRequestOptions): NewSizingRequest | null {
  if (!catalog?.throughput?.length) return null;

  const concurrentUsers = tradeoffs.concurrentUsers ?? 0;
  if (concurrentUsers <= 0) return null;

  // Derive latency mode from tolerance
  const tolerance = tradeoffs.latencyTolerance ?? 'balanced';
  const slaMode = (() => {
    switch (tolerance) {
      case 'aggressive': return 'snappy' as const;
      case 'fast': return 'fast' as const;
      case 'balanced': return 'responsive' as const;
      case 'relaxed': return 'comfy' as const;
      case 'queue':
      case 'none':
      default: return 'async' as const;
    }
  })();

  const defaultBatchForTolerance: Record<string, number> = {
    aggressive: 1,
    fast: 4,
    balanced: 16,
    relaxed: 64,
    queue: 256,
    none: 256,
  };

  const effectiveBatch = defaultBatchForTolerance[tolerance] ?? defaultBatchForTolerance.balanced;
  const mcLatencyMode = slaMode === 'responsive' ? 'resp' : slaMode;

  // Find throughput entry
  const modelId = selectedModelId ?? catalog.models?.[0]?.model_id;
  if (!modelId) return null;

  const precisionBits = Number(tradeoffs.precision ?? 8);
  const backend = catalog.accelerator?.backend ?? null;
  const throughputPoints = catalog.throughput;

  const primaryCandidates = throughputPoints.filter((point) => {
    if (point.model_id !== modelId) return false;
    if (Number(point.precision) !== precisionBits) return false;
    if (backend && (point.backend ?? backend) !== backend) return false;
    return true;
  });

  let throughputEntry = null as (typeof throughputPoints)[number] | null;

  if (primaryCandidates.length > 0) {
    const sorted = [...primaryCandidates].sort((a, b) => (a.batch ?? 0) - (b.batch ?? 0));
    throughputEntry = sorted.find((p) => (p.batch ?? 0) >= effectiveBatch) ?? sorted[sorted.length - 1];
  }

  if (!throughputEntry) {
    const modelBackendCandidates = throughputPoints.filter((point) => {
      if (point.model_id !== modelId) return false;
      if (backend && (point.backend ?? backend) !== backend) return false;
      return true;
    });
    if (modelBackendCandidates.length > 0) {
      const sorted = [...modelBackendCandidates].sort((a, b) => (a.batch ?? 0) - (b.batch ?? 0));
      throughputEntry = sorted.find((p) => (p.batch ?? 0) >= effectiveBatch) ?? sorted[sorted.length - 1];
    }
  }

  if (!throughputEntry) {
    const anyModel = throughputPoints.filter((point) => point.model_id === modelId);
    if (anyModel.length > 0) {
      const sorted = [...anyModel].sort((a, b) => (a.batch ?? 0) - (b.batch ?? 0));
      throughputEntry = sorted.find((p) => (p.batch ?? 0) >= effectiveBatch) ?? sorted[sorted.length - 1];
    }
  }

  throughputEntry = throughputEntry ?? throughputPoints[0] ?? null;
  if (!throughputEntry) return null;

  // Build profiles map
  const profilesById: Record<string, PersonaProfile> = Object.fromEntries(
    personaProfiles.map((p) => [p.id, p]),
  );

  const scenarioUsers = users;
  const personaCount = scenarioUsers.length || 1;
  const turns = tradeoffs.turns ?? 1;

  const effectivePersonas = scenarioUsers.map((user) =>
    resolveEffectivePersona(user, profilesById, slaMode, personaCount),
  );

  const personas = effectivePersonas.map((persona, idx) => {
    const user = scenarioUsers[idx];
    const profile =
      user?.profileId && user.profileId !== 'custom'
        ? profilesById[user.profileId]
        : undefined;

    const population = concurrentUsers * (persona.sharePercent / 100);
    const personaLatencyMode = persona.latencyMode ?? slaMode;
    const latencyKey = personaLatencyMode === 'responsive' ? 'resp' : personaLatencyMode;

    const effectiveLatencyMs = profile
      ? (() => {
          switch (latencyKey) {
            case 'snappy': return toNum((profile as any).e2e_target_snappy_ms);
            case 'fast': return toNum((profile as any).e2e_target_fast_ms);
            case 'resp': return toNum((profile as any).e2e_target_resp_ms);
            case 'comfy': return toNum((profile as any).e2e_target_comfy_ms);
            case 'async': return toNum((profile as any).e2e_target_async_ms);
            default: return null;
          }
        })()
      : null;

    const inputTokensPerConversation = persona.inputTokensPerTurn * turns;
    const outputTokensPerConversation = persona.outputTokensPerTurn * turns;
    const { ttftSec, decodeTokPerSec } = resolvePersonaLatency(profile, latencyKey);

    const streamSec = decodeTokPerSec > 0 ? outputTokensPerConversation / decodeTokPerSec : 0;
    const digestSec = ttftSec + streamSec;

    const latency_profiles = profile
      ? {
          snappy: {
            ttft_ms: toNum((profile as any).ttft_snappy_ms) ?? 0,
            decode_tps: toNum((profile as any).decode_tps_snappy) ?? 0,
          },
          fast: {
            ttft_ms: toNum((profile as any).ttft_fast_ms) ?? 0,
            decode_tps: toNum((profile as any).decode_tps_fast) ?? 0,
          },
          resp: {
            ttft_ms: toNum((profile as any).ttft_resp_ms) ?? 0,
            decode_tps: toNum((profile as any).decode_tps_resp) ?? 0,
          },
          comfy: {
            ttft_ms: toNum((profile as any).ttft_comfy_ms) ?? 0,
            decode_tps: toNum((profile as any).decode_tps_comfy) ?? 0,
          },
          async: {
            ttft_ms: toNum((profile as any).ttft_async_ms) ?? 0,
            decode_tps: toNum((profile as any).decode_tps_async) ?? 0,
          },
        }
      : undefined;

    return {
      id: persona.id ?? `persona-${idx}`,
      name: persona.display_name ?? `Persona ${idx + 1}`,
      display_name: persona.display_name ?? `Persona ${idx + 1}`,
      prompt_tokens_per_turn: persona.inputTokensPerTurn,
      response_tokens_per_turn: persona.outputTokensPerTurn,
      turns_kept: turns,
      read_sec_default: persona.readSec,
      think_sec_default: persona.thinkSec,
      sharePercent: persona.sharePercent,
      population,
      inputTokensPerConversation,
      outputTokensPerConversation,
      readSeconds: persona.readSec,
      thinkSeconds: persona.thinkSec,
      ttftMs: ttftSec * 1000,
      decodeTps: decodeTokPerSec,
      turnsKept: turns,
      latencyTargetMs: effectiveLatencyMs,
      tokens: {
        input: persona.inputTokensPerTurn,
        output: persona.outputTokensPerTurn,
      },
      readSec: persona.readSec,
      thinkSec: persona.thinkSec,
      digestSec,
      turnsPerSession: turns,
      latency_profiles,
      latency_slo_default_ms: profile?.default_latency_slo_ms ?? 0,
      profileId: user?.profileId,
    };
  });

  // Build model data
  const modelRow = catalog.models?.find((m) => m.model_id === modelId) ??
    catalog.models?.find((m) => m.model_id === catalog.models?.[0]?.model_id);

  let kvBytesPerToken = 0;
  let weightsGiB = 0;
  let weightsGiBUnshared = 0;
  let runtimeOverheadGiB = 0;
  let usableKvGiBPerGpu = 0;
  let hbmGiB = 0;
  let fitsWeightsWithoutSharing = false;
  let fitsWeightsWithSharing = false;
  let bridgeGroupSize = 1;
  let weightsSharedWithinGroup = false;

  if (modelRow && catalog.accelerator) {
    const kvMap = parseJson<Record<string, number>>(modelRow.kv_per_token_json) ?? {};
    const weightsMap = parseJson<Record<string, number>>(modelRow.weights_mem_json) ?? {};

    kvBytesPerToken = kvMap[String(precisionBits)] ?? 0;

    const accel = catalog.accelerator as any;

    bridgeGroupSize = Number(accel.bridge_group_size_max ?? 1) || 1;
    weightsSharedWithinGroup = true;

    const weightsBytesRaw = weightsMap[String(precisionBits)] ?? 0;
    weightsGiBUnshared = weightsBytesRaw / BYTES_PER_GIB;

    const weightsBytesPerGpu =
      weightsSharedWithinGroup && bridgeGroupSize > 0
        ? weightsBytesRaw / bridgeGroupSize
        : weightsBytesRaw;

    weightsGiB = weightsBytesPerGpu / BYTES_PER_GIB;

    const hbmB = Number(accel.hbm_bytes ?? 0);
    hbmGiB = hbmB / BYTES_PER_GIB;

    const modelRuntimeBytes = Number(modelRow.base_runtime_overhead_bytes ?? 0);
    const reservedRuntimeBytesPerGpu = Number(accel.reserved_runtime_bytes ?? 0);
    const runtimeGroupBytes = Number(accel.runtime_overhead_bytes_per_group ?? 0);
    const runtimeGroupBytesPerGpu =
      bridgeGroupSize > 0 ? runtimeGroupBytes / bridgeGroupSize : runtimeGroupBytes;

    const totalRuntimeBytesPerGpu =
      modelRuntimeBytes + reservedRuntimeBytesPerGpu + runtimeGroupBytesPerGpu;

    runtimeOverheadGiB = totalRuntimeBytesPerGpu / BYTES_PER_GIB;

    fitsWeightsWithoutSharing = hbmGiB > 0 ? weightsGiBUnshared + runtimeOverheadGiB <= hbmGiB : false;
    fitsWeightsWithSharing = hbmGiB > 0 ? weightsGiB + runtimeOverheadGiB <= hbmGiB : false;
    usableKvGiBPerGpu = Math.max(0, hbmGiB - weightsGiB - runtimeOverheadGiB);
  }

  const model = {
    id: modelRow?.model_id ?? modelId,
    paramsBillions: Number(modelRow?.params_b ?? 0),
    precisionBits,
    kvBytesPerToken,
    weightsGiB,
    usableKvGiBPerGpu,
    hbmGiB,
    weightsGiBUnshared,
    fitsWeightsWithoutSharing,
    fitsWeightsWithSharing,
    bridgeGroupSize,
    weightsSharedWithinGroup,
  };

  const throughput = {
    promptTpsPerGpu: Number(
      (throughputEntry as any).prompt_tps ??
        (throughputEntry as any).promptTps ??
        0,
    ),
    decodeTpsPerGpu: Number(
      (throughputEntry as any).decode_tps ??
        (throughputEntry as any).decodeTps ??
        0,
    ),
  };

  const uiState = {
    conversationTurns: turns,
    batchSize: effectiveBatch,
    latencySloMs: advanced.latencyMs ?? advanced.latencyTargetMs ?? 0,
    mcFastTrials: 8,
    mcRefineTrials: 32,
    mcHorizonSeconds: 600,
    trafficVolatility: 0.2,
  };

  return buildMcRequest({
    personas,
    model,
    throughput,
    uiState,
    scenario: { concurrentUsers },
    latencyMode: mcLatencyMode,
  });
}
